import dayjs from "dayjs";

export const DIAS_SEMANA = [
  "domingo",
  "segunda",
  "terca",
  "quarta",
  "quinta",
  "sexta",
  "sabado",
];

export const DIA_POR_DOW = DIAS_SEMANA;

export const DIA_PARA_FC = {
  domingo: 0,
  segunda: 1,
  terca: 2,
  quarta: 3,
  quinta: 4,
  sexta: 5,
  sabado: 6,
};

export const DEFAULT_BUSINESS_HOURS = {
  daysOfWeek: [1, 2, 3, 4, 5, 6],
  startTime: "08:00",
  endTime: "20:00",
};

export const DEFAULT_SLOT_BOUNDS = {
  slotMinTime: "07:00",
  slotMaxTime: "22:00",
};

export const normalizarHora = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value).slice(0, 5);
};

export const horaParaMinutos = (hora) => {
  const normalizada = normalizarHora(hora);
  if (!normalizada) return null;
  const [h, m] = normalizada.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

export const minutosParaHora = (total) =>
  `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;

export const obterDiaSemana = (dateValue) => {
  const slot = dayjs(dateValue);
  if (!slot.isValid()) return null;
  return DIA_POR_DOW[slot.day()] || null;
};

export const parseHorariosFuncionamento = (respostaApi) => {
  if (!respostaApi || typeof respostaApi !== "object") return {};

  const parseAtivo = (value) =>
    value === true || value === 1 || value === "1" || value === "true";

  return DIAS_SEMANA.reduce((acc, dia) => {
    const cfg = respostaApi[dia] || {};
    acc[dia] = {
      ativo: parseAtivo(cfg.ativo),
      abertura: normalizarHora(cfg.abertura),
      fechamento: normalizarHora(cfg.fechamento),
    };
    return acc;
  }, {});
};

export const buildDisponibilidadePorDia = (horariosAgrupados = {}) => {
  const mapa = new Map();

  DIAS_SEMANA.forEach((dia) => {
    const itens = Array.isArray(horariosAgrupados[dia])
      ? horariosAgrupados[dia]
      : [];
    const horas = new Set(
      itens
        .filter((item) => item?.ativo !== false)
        .map((item) => normalizarHora(item?.hora))
        .filter(Boolean)
    );
    mapa.set(dia, horas);
  });

  return mapa;
};

export const inferirIntervaloSlots = (slotsOrdenados = []) => {
  if (!Array.isArray(slotsOrdenados) || slotsOrdenados.length < 2) return 30;

  let menorIntervalo = Infinity;
  for (let index = 1; index < slotsOrdenados.length; index += 1) {
    const anterior = horaParaMinutos(slotsOrdenados[index - 1]);
    const atual = horaParaMinutos(slotsOrdenados[index]);
    if (anterior === null || atual === null) continue;
    const diff = atual - anterior;
    if (diff > 0) menorIntervalo = Math.min(menorIntervalo, diff);
  }

  return Number.isFinite(menorIntervalo) ? menorIntervalo : 30;
};

export const buildFaixaDisponibilidadePorDia = (disponibilidadePorDia = new Map()) => {
  const mapa = new Map();

  DIAS_SEMANA.forEach((dia) => {
    const slots = [...(disponibilidadePorDia.get(dia) || [])].sort();
    if (!slots.length) {
      mapa.set(dia, null);
      return;
    }

    const intervalo = inferirIntervaloSlots(slots);
    const ultimoSlot = slots[slots.length - 1];
    const fimExclusivoMinutos = horaParaMinutos(ultimoSlot) + intervalo;

    mapa.set(dia, {
      inicio: slots[0],
      fim: ultimoSlot,
      fimExclusivo:
        fimExclusivoMinutos === null
          ? ultimoSlot
          : minutosParaHora(Math.min(24 * 60, fimExclusivoMinutos)),
      slots: new Set(slots),
      intervalo,
    });
  });

  return mapa;
};

const computeBoundsFromFuncionamento = (horariosPorDia = {}) => {
  let minMinutos = null;
  let maxMinutos = null;

  DIAS_SEMANA.forEach((dia) => {
    const cfg = horariosPorDia[dia];
    if (!cfg?.ativo) return;

    const abertura = horaParaMinutos(cfg.abertura);
    const fechamento = horaParaMinutos(cfg.fechamento);
    if (abertura === null || fechamento === null || fechamento <= abertura) return;

    minMinutos = minMinutos === null ? abertura : Math.min(minMinutos, abertura);
    maxMinutos = maxMinutos === null ? fechamento : Math.max(maxMinutos, fechamento);
  });

  if (minMinutos === null || maxMinutos === null) return null;

  return {
    slotMinTime: minutosParaHora(minMinutos),
    slotMaxTime: minutosParaHora(Math.min(24 * 60, maxMinutos)),
  };
};

export const computeCalendarBounds = (
  disponibilidadePorDia = new Map(),
  horariosPorDia = {}
) => {
  let minMinutos = null;
  let maxMinutos = null;

  disponibilidadePorDia.forEach((slots) => {
    const ordenados = [...slots].sort();
    if (!ordenados.length) return;

    const intervalo = inferirIntervaloSlots(ordenados);
    const primeiro = horaParaMinutos(ordenados[0]);
    const ultimo = horaParaMinutos(ordenados[ordenados.length - 1]);
    if (primeiro === null || ultimo === null) return;

    minMinutos = minMinutos === null ? primeiro : Math.min(minMinutos, primeiro);
    maxMinutos =
      maxMinutos === null
        ? ultimo + intervalo
        : Math.max(maxMinutos, ultimo + intervalo);
  });

  if (minMinutos !== null && maxMinutos !== null) {
    return {
      slotMinTime: minutosParaHora(minMinutos),
      slotMaxTime: minutosParaHora(Math.min(24 * 60, maxMinutos)),
    };
  }

  return computeBoundsFromFuncionamento(horariosPorDia) || { ...DEFAULT_SLOT_BOUNDS };
};

export const buildBusinessHoursFromDisponibilidade = (
  disponibilidadePorDia = new Map()
) => {
  const businessHours = [];

  DIAS_SEMANA.forEach((dia) => {
    const slots = [...(disponibilidadePorDia.get(dia) || [])].sort();
    if (!slots.length) return;

    const intervalo = inferirIntervaloSlots(slots);
    const fimMinutos = horaParaMinutos(slots[slots.length - 1]);
    if (fimMinutos === null) return;

    businessHours.push({
      daysOfWeek: [DIA_PARA_FC[dia]],
      startTime: slots[0],
      endTime: minutosParaHora(Math.min(24 * 60, fimMinutos + intervalo)),
    });
  });

  return businessHours;
};

const UNAVAILABLE_BG = "#edeff0";

const criarFundoIndisponivel = ({ id, start, end, message }) => ({
  id,
  start: start?.toISOString?.() || start,
  end: end?.toISOString?.() || end,
  display: "background",
  backgroundColor: UNAVAILABLE_BG,
  borderColor: UNAVAILABLE_BG,
  classNames: ["fc-fz-unavailable-bg"],
  extendedProps: {
    isFuncionamentoFechado: true,
    blockReason: message,
  },
});

export const buildBackgroundEventsDiasFechados = (
  horariosPorDia = {},
  intervaloVisivel,
  { slotMinTime = "07:00", slotMaxTime = "22:00" } = {}
) => {
  if (!intervaloVisivel?.start || !intervaloVisivel?.end) return [];

  const eventos = [];
  let cursor = dayjs(intervaloVisivel.start).startOf("day");
  const limite = dayjs(intervaloVisivel.end);

  while (cursor.isBefore(limite)) {
    const dia = obterDiaSemana(cursor);
    const cfg = horariosPorDia[dia] || {};
    const data = cursor.format("YYYY-MM-DD");

    if (!cfg.ativo) {
      const inicio = dayjs(`${data}T${slotMinTime}:00`);
      const fim = dayjs(`${data}T${slotMaxTime}:00`);

      if (inicio.isValid() && fim.isAfter(inicio)) {
        eventos.push(
          criarFundoIndisponivel({
            id: `funcionamento-fechado-${data}`,
            start: inicio,
            end: fim,
            message: "Estabelecimento fechado neste dia.",
          })
        );
      }
    }

    cursor = cursor.add(1, "day");
  }

  return eventos;
};

/** @deprecated use buildBusinessHoursFromDisponibilidade */
export const buildBusinessHoursFromFuncionamento = (horariosPorDia = {}) => {
  const businessHours = [];

  DIAS_SEMANA.forEach((dia) => {
    const cfg = horariosPorDia[dia];
    if (!cfg?.ativo || !cfg.abertura || !cfg.fechamento) return;

    const abertura = normalizarHora(cfg.abertura);
    const fechamento = normalizarHora(cfg.fechamento);
    if (!abertura || !fechamento || abertura >= fechamento) return;

    businessHours.push({
      daysOfWeek: [DIA_PARA_FC[dia]],
      startTime: abertura,
      endTime: fechamento,
    });
  });

  return businessHours;
};

export const slotForaDoExpediente = (dateValue, horariosPorDia = {}) => {
  const slot = dayjs(dateValue);
  if (!slot.isValid()) {
    return { bloqueado: true, message: "Horário inválido." };
  }

  const dia = obterDiaSemana(slot);
  const cfg = horariosPorDia[dia] || {};
  const hora = slot.format("HH:mm");

  if (!cfg.ativo) {
    return {
      bloqueado: true,
      message: "Estabelecimento fechado neste dia.",
    };
  }

  const abertura = normalizarHora(cfg.abertura);
  const fechamento = normalizarHora(cfg.fechamento);

  if (!abertura || !fechamento) {
    return {
      bloqueado: true,
      message: "Horário de funcionamento não configurado para este dia.",
    };
  }

  if (hora < abertura || hora >= fechamento) {
    return {
      bloqueado: true,
      message: `Fora do horário de funcionamento (${abertura} às ${fechamento}).`,
    };
  }

  return { bloqueado: false, message: "" };
};

export const slotEstaForaDaFaixaVisual = (
  dateValue,
  faixaDisponibilidadePorDia = new Map()
) => {
  const slot = dayjs(dateValue);
  if (!slot.isValid()) return false;

  const dia = obterDiaSemana(slot);
  const hora = slot.format("HH:mm");
  const faixa = faixaDisponibilidadePorDia.get(dia);

  if (!faixa) return false;

  return hora < faixa.inicio || hora >= faixa.fimExclusivo;
};

/** Usado só para feedback visual; não bloqueia clique no admin. */
export const slotTemDisponibilidade = (
  dateValue,
  faixaDisponibilidadePorDia = new Map()
) => {
  const slot = dayjs(dateValue);
  if (!slot.isValid()) {
    return { disponivel: false, message: "Horário inválido." };
  }

  const dia = obterDiaSemana(slot);
  const hora = slot.format("HH:mm");
  const faixa = faixaDisponibilidadePorDia.get(dia);

  if (!faixa) {
    return {
      disponivel: false,
      message: "Nenhum horário de reserva configurado para este dia.",
    };
  }

  if (hora < faixa.inicio) {
    return {
      disponivel: false,
      message: `Fora da disponibilidade deste dia (início às ${faixa.inicio}).`,
    };
  }

  if (hora >= faixa.fimExclusivo) {
    return {
      disponivel: false,
      message: `Fora da disponibilidade deste dia (término às ${faixa.fim}).`,
    };
  }

  if (!faixa.slots.has(hora)) {
    return {
      disponivel: false,
      message: `Horário ${hora} não está na grade de disponibilidade.`,
    };
  }

  return { disponivel: true, message: "" };
};
